// UploadBillModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaUpload, FaTimes, FaFilePdf, FaImage } from 'react-icons/fa';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Expenses/UploadBillModal.css';

const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

const UploadBillModal = ({ expense, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [billNumber, setBillNumber] = useState(expense?.bill?.billNumber || '');
  const [error, setError] = useState('');
  const dropRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hasExisting = !!expense?.bill?.fileUrl;
  const title = hasExisting ? 'Replace Bill' : 'Upload Bill';

  const previewType = useMemo(() => {
    if (!file) return null;
    if (file.type === 'application/pdf') return 'pdf';
    if (file.type.startsWith('image/')) return 'image';
    return 'other';
  }, [file]);

  const objectUrl = useMemo(() => {
    if (previewType === 'image' && file) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [previewType, file]);

  useEffect(() => () => objectUrl && URL.revokeObjectURL(objectUrl), [objectUrl]);

  const validate = (f) => {
    if (!f) return 'Please select a file.';
    if (!ALLOWED.includes(f.type)) return 'Only PDF, JPG, PNG, or WEBP allowed.';
    if (f.size > MAX_SIZE) return 'Max file size is 10MB.';
    return '';
  };

  const pickFile = (f) => {
    const err = validate(f);
    setError(err);
    setFile(err ? null : f);
  };

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    pickFile(f || null);
  };

  // Drag & drop
  const onDragOver = (e) => {
    e.preventDefault();
    dropRef.current?.classList.add('is-dragover');
  };
  const onDragLeave = () => dropRef.current?.classList.remove('is-dragover');
  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove('is-dragover');
    const f = e.dataTransfer.files?.[0];
    pickFile(f || null);
  };

  const submit = () => {
    if (!file) {
      setError('Please select a bill file.');
      return;
    }
    const sanitized = billNumber.trim() || undefined;
    onUpload({
      expenseId: expense._id,
      file,
      billNumber: sanitized,
    });
  };

  const stopOverlayClose = (e) => {
    if (e.currentTarget === e.target) onClose?.();
  };

  return (
    <div className="ubm-overlay" onMouseDown={stopOverlayClose}>
      <div className="ubm-modal" role="dialog" aria-modal="true" aria-labelledby="ubm-title">
        <div className="ubm-header">
          <h2 id="ubm-title">{title}</h2>
          <button
            className="ubm-icon-btn ubm-close"
            aria-label="Close"
            title="Close"
            onClick={onClose}
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <div className="ubm-body">
          {/* Bill Number */}
          <label className="ubm-field">
            <span>Bill Number <em className="ubm-muted">(optional)</em></span>
            <input
              type="text"
              placeholder="e.g. INV-2025/0042"
              value={billNumber}
              onChange={(e) => setBillNumber(e.target.value)}
              maxLength={32}
            />
            <div className="ubm-help">{billNumber.length}/32</div>
          </label>

          {/* Dropzone */}
          <div
            ref={dropRef}
            className="ubm-dropzone"
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <div className="ubm-drop-inner">
              <FaUpload className="ubm-drop-icon" />
              <div className="ubm-drop-text">
                <strong>Drag & drop</strong> your bill here, or
                <label className="ubm-file-link">
                  <input
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/webp"
                    onChange={onInputChange}
                  />
                  browse
                </label>
              </div>
              <div className="ubm-drop-sub">PDF, JPG, PNG, or WEBP • up to 10MB</div>
            </div>
          </div>

          {/* Preview / Selected file */}
          {file && (
            <div className="ubm-preview">
              {previewType === 'image' ? (
                <img src={objectUrl} alt="Selected bill" />
              ) : (
                <div className="ubm-doc">
                  {previewType === 'pdf' ? <FaFilePdf /> : <FaImage />}
                </div>
              )}
              <div className="ubm-meta">
                <div className="ubm-name" title={file.name}>{file.name}</div>
                <div className="ubm-size">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB • {file.type || 'file'}
                </div>
              </div>
              <button
                className="ubm-icon-btn ubm-remove"
                aria-label="Remove file"
                title="Remove file"
                onClick={() => { setFile(null); setError(''); }}
                type="button"
              >
                <FaTimes />
              </button>
            </div>
          )}

          {/* Error */}
          {error && <div className="ubm-error">{error}</div>}
        </div>

        <div className="ubm-footer">
          <button
            className="ubm-primary"
            onClick={submit}
            disabled={!file || !!error}
            type="button"
          >
            Save
          </button>
          <button className="ubm-cancel" onClick={onClose} type="button">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default UploadBillModal;
